import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "~/utils/trpc";
import { CenterModal } from "../CenterModal";
import {
  MODAL_FORM_LAYOUT,
  modalFormFieldLabelSx,
  modalFormIntroBodySx,
  modalFormMultilineTextFieldSx,
  modalFormSelectSx,
  modalFormTextFieldSx,
} from "../modalFormStyles";
import { useTeamProject } from "../TeamProjectProvider";
import { useToast } from "../ToastProvider";

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  onCreated?: (projectId: string) => void;
}

export default function CreateProjectModal({
  open,
  onClose,
  teamId,
  onCreated,
}: CreateProjectModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const utils = trpc.useUtils();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [description, setDescription] = useState("");
  const [channelId, setChannelId] = useState("");
  const [channelIdError, setChannelIdError] = useState("");
  const [channelError, setChannelError] = useState("");
  const [showGuide, setShowGuide] = useState(false);

  const { projects } = useTeamProject();
  const { data: archivedProjects } = trpc.project.listArchived.useQuery(
    { teamId },
    { enabled: open },
  );
  const usedChannelIds = new Set(
    [...projects, ...(archivedProjects ?? [])]
      .filter((p) => p.messengerChannelId)
      .map((p) => p.messengerChannelId),
  );

  // Check if the team has a messenger server linked
  const { data: teams } = trpc.team.list.useQuery(undefined, { enabled: open });
  const currentTeam = teams?.find((t) => t.id === teamId);
  const hasServer = Boolean(currentTeam?.messengerServerId);
  const isSlack = currentTeam?.platformType === "slack";

  // Fetch messenger channels only when team has a server linked
  const { data: messengerChannels, isLoading: channelsLoading } =
    trpc.project.messengerChannels.useQuery(
      { teamId },
      { enabled: open && hasServer },
    );

  const linkChannelMutation = trpc.project.linkChannel.useMutation();

  const createMutation = trpc.project.create.useMutation({
    onSuccess: async (project) => {
      const selectedChannel = messengerChannels?.find(
        (c) => c.id === channelId,
      );
      if (channelId) {
        await linkChannelMutation.mutateAsync({
          projectId: project.id,
          messengerChannelId: channelId,
          messengerChannelName: selectedChannel?.name ?? channelId,
          platformType: currentTeam?.platformType ?? "discord",
        });
      }
      showToast(t("project.createSuccess", { name: project.name }), "success");
      utils.project.list.invalidate({ teamId });
      onCreated?.(project.id);
      handleClose();
    },
    onError: (err) => showToast(err.message, "error"),
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError(t("project.nameRequired"));
      return;
    }
    if (!channelId.trim()) {
      setChannelError(t("project.channelRequired"));
      return;
    }
    if (!hasServer) {
      const trimmed = channelId.trim();
      if (isSlack && !/^[A-Z0-9]{9,12}$/i.test(trimmed)) {
        setChannelIdError(t("slack.invalidChannelId"));
        return;
      }
      if (!isSlack && !/^\d{17,20}$/.test(trimmed)) {
        setChannelIdError(t("discord.invalidChannelId"));
        return;
      }
    }
    if (usedChannelIds.has(channelId)) {
      setChannelError(t("project.channelAlreadyLinked"));
      return;
    }
    setNameError("");
    setChannelIdError("");
    setChannelError("");
    createMutation.mutate({
      teamId,
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  const handleClose = () => {
    setName("");
    setNameError("");
    setDescription("");
    setChannelId("");
    setChannelIdError("");
    setChannelError("");
    setShowGuide(false);
    onClose();
  };

  const isPending = createMutation.isPending || linkChannelMutation.isPending;

  const labelSx = modalFormFieldLabelSx(theme);
  const inputSx = modalFormTextFieldSx(theme);
  const multilineSx = modalFormMultilineTextFieldSx(theme);
  const selectSx = modalFormSelectSx(theme);

  return (
    <CenterModal
      open={open}
      onClose={handleClose}
      title={t("project.create")}
      size="md"
      width={480}
      hideCloseButton
    >
      <Box>
        <Stack spacing={MODAL_FORM_LAYOUT.fieldStackGap}>
          <Typography variant="body1" sx={modalFormIntroBodySx(theme)}>
            {t("project.channelDescription")}
          </Typography>

          {/* Project name */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.name")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
              sx={inputSx}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.description")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              sx={multilineSx}
            />
          </Box>

          {/* Channel section */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.channel")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>

            {hasServer ? (
              /* Channel dropdown when server is linked */
              <Box>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={channelId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setChannelId(id);
                    setChannelError("");
                    const ch = messengerChannels?.find((c) => c.id === id);
                    if (ch && !name.trim()) setName(ch.name);
                  }}
                  disabled={channelsLoading}
                  error={Boolean(channelError)}
                  sx={selectSx}
                >
                <MenuItem value="">
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.custom.gray[500] }}
                  >
                    {channelsLoading
                      ? t("common.loading")
                      : t("common.selectChannel")}
                  </Typography>
                </MenuItem>
                {messengerChannels?.map((ch) => {
                  const isUsed = usedChannelIds.has(ch.id);
                  return (
                    <MenuItem key={ch.id} value={ch.id} disabled={isUsed}>
                      # {ch.name}{" "}
                      {isUsed ? t("project.channelAlreadyLinked") : ""}
                    </MenuItem>
                  );
                })}
              </Select>
              {channelError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {channelError}
                </Typography>
              )}
            </Box>
          ) : (
            /* Manual input when no server linked */
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  isSlack
                    ? t("slack.channelIdPlaceholder")
                    : t("discord.channelIdPlaceholder")
                }
                value={channelId}
                onChange={(e) => {
                  setChannelId(e.target.value);
                  setChannelIdError("");
                  setChannelError("");
                }}
                error={Boolean(channelIdError || channelError)}
                helperText={
                  channelIdError || channelError || t("project.channelIdHelper")
                }
                sx={inputSx}
              />

              <Box
                mt={1}
                sx={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: theme.palette.custom.gray[500],
                  "&:hover": { color: theme.palette.custom.primary },
                }}
                onClick={() => setShowGuide((v) => !v)}
              >
                {showGuide ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
                <Typography variant="caption" fontWeight={500}>
                  {t(isSlack ? "slack.howToFind" : "discord.howToFind")}
                </Typography>
              </Box>
              <Collapse in={showGuide}>
                <Box
                  mt={1}
                  p={1.5}
                  sx={{
                    bgcolor: theme.palette.custom.gray[100],
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: theme.palette.custom.divider,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-line", display: "block" }}
                  >
                    {t(
                      isSlack
                        ? "slack.howToFindGuide"
                        : "discord.howToFindGuide",
                    )}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
        </Stack>

        {/* Actions */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{
            mt: MODAL_FORM_LAYOUT.gapFieldSectionToActions,
            pb: MODAL_FORM_LAYOUT.actionsPaddingBottom,
          }}
        >
          <Button
            variant="text"
            onClick={handleClose}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: theme.palette.custom.primary,
              px: 2,
              "&:hover": {
                bgcolor: theme.palette.custom.primarySecondaryHover,
              },
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!name.trim() || !channelId || isPending}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "0.5rem",
              bgcolor: theme.palette.custom.primary,
              px: 3,
              "&:hover": { bgcolor: theme.palette.custom.primaryHover },
              "&.Mui-disabled": {
                bgcolor: theme.palette.custom.primaryDisabled,
                color: "#fff",
              },
            }}
          >
            {t("project.createButton")}
          </Button>
        </Stack>
      </Box>
    </CenterModal>
  );
}
